/**
 * Products API client.
 *
 * Namespace: /api/web-admin/products/
 *
 * All monetary fields (price, cost, etc.) are strings (Decimal on backend).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-client';

import { api } from './client';
import type { ProductPhotoVersion } from './stock/barcodes';

// =============================================================================
// TYPES
// =============================================================================

export type SourceType = 'gopos' | 'manual';
export type SyncStatusType = 'synced' | 'pending' | 'not_synced' | 'error';
export type IssueType = 'price_mismatch' | 'vat_mismatch' | 'coverage_gap' | 'needs_review';
export type GroupByType = 'name' | 'none';
export type SortByType =
  | 'name'
  | '-name'
  | 'price'
  | '-price'
  | 'category'
  | '-category'
  | 'sales_7d'
  | '-sales_7d'
  | 'cost'
  | '-cost'
  | 'margin'
  | '-margin'
  | 'stock'
  | '-stock'
  | 'venue_count'
  | '-venue_count'
  | 'issue_count'
  | '-issue_count';

export type ApiSortByType =
  | 'name'
  | '-name'
  | 'price'
  | '-price'
  | 'sales_7d'
  | '-sales_7d'
  | 'venue_count'
  | '-venue_count'
  | 'issue_count'
  | '-issue_count';

const API_SORT_VALUES: ReadonlySet<ApiSortByType> = new Set([
  'name',
  '-name',
  'price',
  '-price',
  'sales_7d',
  '-sales_7d',
  'venue_count',
  '-venue_count',
  'issue_count',
  '-issue_count',
]);

export function isApiSortBy(value: SortByType | undefined): value is ApiSortByType {
  return value != null && API_SORT_VALUES.has(value as ApiSortByType);
}

export function toApiSortBy(value: SortByType | undefined): ApiSortByType | undefined {
  return isApiSortBy(value) ? value : undefined;
}

export interface CategoryRef {
  id: string;
  name: string;
}

/** ERP-only shelf group inside a till category; never sent to GoPOS (MD-0115). */
export interface SubcategoryRef {
  id: string;
  name: string;
}

export interface ProductListItem {
  id: string;
  name: string;
  category: CategoryRef | null;
  subcategory?: SubcategoryRef | null;
  canonical_price: string | null; // Decimal as string
  unit_code: string | null;
  is_weighted?: boolean;
  is_active: boolean;
  is_produced: boolean;
  source: SourceType;
  gopos_sync_status: SyncStatusType;
  has_price_mismatch: boolean;
  has_vat_mismatch: boolean;
  has_coverage_gap: boolean;
  needs_review: boolean;
  issue_types: string[];
  issue_count: number;
  venue_count: number;
  price_min: string | null; // Decimal as string
  price_max: string | null; // Decimal as string
  /** Stores with their own price, set in ERP on purpose after the network price. */
  own_prices?: StoreOwnPrice[];
  tax_rate: string | null; // Decimal as string
  vat_rates: string[]; // Decimals as strings
  sales_7d: string | null; // Decimal as string
  cost?: string | null; // Decimal as string
  cost_gross?: string | null; // Client-derived Decimal as string
  margin?: string | null; // Decimal as string (%)
  is_cost_partial?: boolean;
  stock_total?: string | null; // Decimal as string (client-computed)
  product_ids: string[] | null;
  /** Current pack photo per side; absent in responses that do not carry photos. */
  photos?: ProductPhotoVersion[];
}

export interface StoreOwnPrice {
  venue_mapping_id: string;
  store_name: string;
  price: string; // Decimal as string
}

export interface ProductDetail extends ProductListItem {
  description: string | null;
  scale_product_code: string | null;
  default_cost: string | null; // Decimal as string
  gopos_synced_at: string | null;
  recipe_id: string | null;
}

export interface ProductSales7dItem {
  venue_mapping_id: string;
  sales_7d: string | null; // Decimal as string
}

export interface ProductSales7dResponse {
  request_id: string;
  product_id: string;
  total_sales_7d: string | null; // Decimal as string
  items: ProductSales7dItem[];
}

export type ProductWorkspaceTab =
  | 'overview'
  | 'stock'
  | 'purchases'
  | 'sales'
  | 'production'
  | 'recipe'
  | 'info';

export interface VenuePrice {
  venue_mapping_id: string;
  location_id: string;
  venue_name: string;
  is_present: boolean;
  gopos_id: number | null;
  price: string | null; // Decimal as string
  /** OWN: the store keeps its own price when the network price changes. */
  price_mode?: 'NETWORK' | 'OWN' | null;
  tax_rate: string | null; // Decimal as string
}

export interface Barcode {
  id?: string;
  barcode: string;
  barcode_type: 'EAN13' | 'EAN8' | 'PLU' | 'INTERNAL';
  venue_mapping_id: string | null;
}

export interface Paging {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export interface ProductPurchaseOverviewSupplier {
  supplier_id: string;
  supplier_name: string;
  supplier_short_name: string | null;
  supplier_full_name: string | null;
  supplier_product_name: string | null;
  latest_unit_price: string | null;
  latest_price_date: string | null;
  first_purchase_date: string | null;
  last_purchase_date: string | null;
  purchase_count: number;
  total_amount: string | null;
  total_amount_gross: string | null;
  avg_unit_price: string | null;
}

export interface ProductPurchasePriceStats {
  current_price: string | null;
  current_price_date: string | null;
  min_price: string | null;
  max_price: string | null;
  avg_price: string | null;
  period_days: number;
  data_points: number;
}

export interface ProductPurchaseOverviewResponse {
  request_id: string;
  product_id: string;
  suppliers: ProductPurchaseOverviewSupplier[];
  price_stats: ProductPurchasePriceStats;
}

export interface ProductPurchaseSupplierSummary {
  supplier_id: string;
  supplier_name: string;
  supplier_short_name: string | null;
  supplier_full_name: string | null;
  latest_unit_price: string | null;
  latest_price_date: string | null;
  first_purchase_date: string | null;
  last_purchase_date: string | null;
  purchase_count: number;
  total_amount: string | null;
  total_amount_gross: string | null;
  avg_unit_price: string | null;
}

export interface ProductPurchaseSummaryResponse {
  request_id: string;
  product_id: string;
  suppliers: ProductPurchaseSupplierSummary[];
  price_stats: ProductPurchasePriceStats;
}

export interface ProductPurchaseHistoryItem {
  date: string;
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_product_name: string | null;
  location_id: string | null;
  location_name: string | null;
  quantity: string;
  unit_code: string | null;
  unit_price: string | null;
  total_price: string | null;
  vat_rate: string | null;
  receipt_id: string;
}

export interface ProductPurchaseHistoryResponse {
  request_id: string;
  items: ProductPurchaseHistoryItem[];
  paging: Paging;
}

export interface ProductRecipeUsageItem {
  recipe_id: string;
  recipe_name: string;
  product_name: string | null;
  product_category_name: string | null;
  ingredient_quantity: string | null;
  ingredient_unit_code: string;
  recipe_yield_quantity: string | null;
  recipe_yield_unit_code: string | null;
  is_active: boolean;
}

export interface ProductRecipeUsageSummary {
  direct_count: number;
  intermediate_count: number;
  final_count: number;
  total_count: number;
}

export interface ProductRecipeUsageResponse {
  request_id: string;
  items: ProductRecipeUsageItem[];
  direct_items: ProductRecipeUsageItem[];
  intermediate_items: ProductRecipeUsageItem[];
  final_items: ProductRecipeUsageItem[];
  summary: ProductRecipeUsageSummary;
}

export interface ProductListResponse {
  items: ProductListItem[];
  paging: Paging;
  total_active_venues: number;
}

export interface IssuesSummary {
  total_products: number;
  products_with_issues: number;
  total_with_issues: number;
  price_mismatch_count: number;
  vat_mismatch_count: number;
  coverage_gap_count: number;
  needs_review_count: number;
}

export interface SubcategoryListItem {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  product_count: number;
  active_product_count: number;
}

export interface CategoryListItem {
  id: string;
  name: string;
  is_active?: boolean;
  product_count: number;
  active_product_count?: number;
  retired_at?: string | null;
  retirement_reason?: string | null;
  subcategories?: SubcategoryListItem[];
}

export interface SubcategoryPayload {
  name?: string;
  description?: string | null;
  sort_order?: number;
}

export interface ProductSubcategoryResponse {
  product_id: string;
  category_id: string | null;
  subcategory: SubcategoryListItem | null;
}

export interface CategoryDeactivateResponse extends CategoryListItem {
  deactivated_product_count: number;
}

export interface ProductUpsertResponse {
  id: string;
  name: string;
  category_id: string | null;
  category_name: string | null;
  scale_product_code: string | null;
  price: string | null; // Decimal as string
  unit: string | null;
  barcodes: string[];
  is_active: boolean;
  sync_status: SyncStatusType;
  venues_synced: number;
  venues_total: number;
}

export interface ProductStatusResponse {
  id: string;
  is_active: boolean;
  sync_status: SyncStatusType;
  venues_synced: number;
  venues_total: number;
}

// =============================================================================
// UOM CHANGE (WIZARD)
// =============================================================================

export interface ProductUomCutoverLocationSummary {
  location_id: string;
  old_balance: string; // Decimal as string
  new_balance: string; // Decimal as string
}

export interface ProductUomCutoverCreatedMovements {
  close: string[];
  open: string[];
}

export interface ProductUomLabelFixResponse {
  event_id: string;
  event_type: 'LABEL_FIX';
  product_id: string;
  from_unit_code: string | null;
  to_unit_code: string;
  cutover_at: string | null;
  product_unit_code: string | null;
  product_is_weighted: boolean;
}

export interface ProductUomCutoverResponse {
  event_id: string;
  event_type: 'CUTOVER';
  product_id: string;
  from_unit_code: string | null;
  to_unit_code: string;
  cutover_at: string;
  uom_locked_before_at: string | null;
  product_unit_code: string | null;
  product_is_weighted: boolean;
  balances_by_location: Record<string, string>; // Decimal as string
  summary: ProductUomCutoverLocationSummary[];
  created_movement_ids?: ProductUomCutoverCreatedMovements | null;
}

export interface ProductDeleteResponse {
  deleted: boolean;
  sync_status: SyncStatusType;
  message: string;
}

export interface CategoryDeleteResponse {
  success: boolean;
}

export interface BarcodeCreateResponse {
  id: string;
  barcode: string;
  type: string;
  sync_status: SyncStatusType;
}

export interface BarcodeDeleteResponse {
  deleted: boolean;
  sync_status: SyncStatusType;
}

export interface ProductSyncVenueStatus {
  venue_mapping_id: string;
  venue_name: string | null;
  sync_status: SyncStatusType;
  sync_error: string | null;
  sync_attempted_at: string | null;
  last_push_at: string | null;
  last_push_version: number | null;
}

export interface ProductSyncStatusResponse {
  id: string;
  sync_status: SyncStatusType;
  venues_synced: number;
  venues_total: number;
  venues: ProductSyncVenueStatus[];
}

export interface ProductsFilters {
  category_id?: string;
  subcategory_id?: string;
  is_active?: boolean;
  has_price_mismatch?: boolean;
  has_mismatch?: boolean;
  needs_review?: boolean;
  has_issues?: boolean;
  issue_types?: IssueType[];
  source?: SourceType;
  is_produced?: boolean;
  search?: string;
  group_by?: GroupByType;
  sort_by?: SortByType;
  include_cost_metrics?: boolean;
  limit?: number;
  offset?: number;
}

export interface ProductPurchaseHistoryFilters {
  supplier_id?: string;
  location_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  limit?: number;
  offset?: number;
}

export interface ProductPurchaseOverviewOptions {
  days?: number;
  enabled?: boolean;
}

export interface ProductPurchaseSummaryOptions extends ProductPurchaseOverviewOptions {
  as_of_date?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

export async function fetchProducts(filters: ProductsFilters = {}): Promise<ProductListResponse> {
  return api.webAdmin.get<ProductListResponse>('/products', {
    params: {
      category_id: filters.category_id,
      subcategory_id: filters.subcategory_id,
      is_active: filters.is_active,
      has_price_mismatch: filters.has_price_mismatch,
      has_mismatch: filters.has_mismatch,
      needs_review: filters.needs_review,
      has_issues: filters.has_issues,
      issue_types: filters.issue_types,
      source: filters.source,
      is_produced: filters.is_produced,
      search: filters.search,
      group_by: filters.group_by,
      sort_by: toApiSortBy(filters.sort_by),
      include_cost_metrics: filters.include_cost_metrics,
      limit: filters.limit,
      offset: filters.offset,
    },
  });
}

export async function fetchProduct(id: string): Promise<ProductDetail> {
  return api.webAdmin.get<ProductDetail>(`/products/${id}`);
}

export async function fetchProductSales7d(id: string): Promise<ProductSales7dResponse> {
  return api.webAdmin.get<ProductSales7dResponse>(`/products/${id}/sales-7d`);
}

export async function fetchProductPrices(
  id: string,
  options: { grouped?: boolean } = {},
): Promise<VenuePrice[]> {
  return api.webAdmin.get<VenuePrice[]>(`/products/${id}/prices`, {
    params: {
      grouped: options.grouped,
    },
  });
}

export async function fetchProductBarcodes(id: string): Promise<Barcode[]> {
  return api.webAdmin.get<Barcode[]>(`/products/${id}/barcodes`);
}

export async function fetchProductPurchaseOverview(
  id: string,
  options: { days?: number } = {},
): Promise<ProductPurchaseOverviewResponse> {
  return api.webAdmin.get<ProductPurchaseOverviewResponse>(`/products/${id}/purchase-overview`, {
    params: {
      days: options.days,
    },
  });
}

export async function fetchProductPurchaseSummary(
  id: string,
  options: ProductPurchaseSummaryOptions = {},
): Promise<ProductPurchaseSummaryResponse> {
  return api.webAdmin.get<ProductPurchaseSummaryResponse>(`/products/${id}/purchase-summary`, {
    params: {
      days: options.days,
      as_of_date: options.as_of_date,
    },
  });
}

export async function fetchProductPurchaseHistory(
  id: string,
  filters: ProductPurchaseHistoryFilters = {},
): Promise<ProductPurchaseHistoryResponse> {
  return api.webAdmin.get<ProductPurchaseHistoryResponse>(`/products/${id}/purchase-history`, {
    params: {
      supplier_id: filters.supplier_id,
      location_id: filters.location_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
      page: filters.page,
      page_size: filters.page_size,
      limit: filters.limit,
      offset: filters.offset,
    },
  });
}

export async function fetchProductRecipeUsage(id: string): Promise<ProductRecipeUsageResponse> {
  return api.webAdmin.get<ProductRecipeUsageResponse>(`/products/${id}/recipe-usage`);
}

export async function fetchCategories(): Promise<CategoryListItem[]> {
  return api.webAdmin.get<CategoryListItem[]>('/products/categories');
}

export interface IssuesSummaryFilters {
  category_id?: string | undefined;
}

export async function fetchIssuesSummary(filters?: IssuesSummaryFilters): Promise<IssuesSummary> {
  return api.webAdmin.get<IssuesSummary>('/products/issues-summary', {
    params: {
      category_id: filters?.category_id,
    },
  });
}

// =============================================================================
// HOOKS
// =============================================================================

export function useProducts(filters: ProductsFilters = {}) {
  return useQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: () => fetchProducts(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useProduct(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.products.detail(id ?? ''),
    queryFn: () => fetchProduct(id!),
    enabled: !!id,
  });
}

export function useProductSales7d(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.products.sales7d(id ?? ''),
    queryFn: () => fetchProductSales7d(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useProductPrices(
  id: string | null | undefined,
  options: { grouped?: boolean } = {},
) {
  const grouped = options.grouped ?? false;
  return useQuery({
    queryKey: queryKeys.products.prices(id ?? '', grouped),
    queryFn: () => fetchProductPrices(id!, { grouped }),
    enabled: !!id,
  });
}

export function useProductBarcodes(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.products.barcodes(id ?? ''),
    queryFn: () => fetchProductBarcodes(id!),
    enabled: !!id,
  });
}

export function useProductPurchaseOverview(
  id: string | null | undefined,
  options: ProductPurchaseOverviewOptions = {},
) {
  const days = options.days ?? 30;
  const enabled = options.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.products.purchaseOverview(id ?? '', days),
    queryFn: () => fetchProductPurchaseOverview(id!, { days }),
    enabled: !!id && enabled,
    staleTime: 60_000,
  });
}

export function useProductPurchaseSummary(
  id: string | null | undefined,
  options: ProductPurchaseSummaryOptions = {},
) {
  const days = options.days ?? 30;
  const enabled = options.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.products.purchaseSummary(id ?? '', days),
    queryFn: () => fetchProductPurchaseSummary(id!, { days }),
    enabled: !!id && enabled,
    staleTime: 60_000,
  });
}

export function useProductPurchaseHistory(
  id: string | null | undefined,
  filters: ProductPurchaseHistoryFilters = {},
) {
  return useQuery({
    queryKey: queryKeys.products.purchaseHistory(id ?? '', filters),
    queryFn: () => fetchProductPurchaseHistory(id!, filters),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

export function useProductRecipeUsage(
  id: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.products.recipeUsage(id ?? ''),
    queryFn: () => fetchProductRecipeUsage(id!),
    enabled: !!id && (options.enabled ?? true),
  });
}

export function useCategories(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.products.categories(),
    queryFn: fetchCategories,
    enabled: options.enabled ?? true,
    staleTime: 300_000,
  });
}

export function useIssuesSummary(
  filters?: IssuesSummaryFilters,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: queryKeys.products.issuesSummary(filters?.category_id),
    queryFn: () => fetchIssuesSummary(filters),
    enabled,
    staleTime: 30_000,
  });
}

// =============================================================================
// MUTATION TYPES
// =============================================================================

/**
 * Payload for creating a new product.
 */
export interface CreateProductPayload {
  name: string;
  category_id: string;
  price: string;
  unit: string;
  tax_rate: string;
  scale_product_code?: string | null;
  barcodes?: string[];
  is_active?: boolean;
  /** The supplier and its article of the invoice line the card is made for. */
  supplier_id?: string;
  supplier_article?: string;
  /** Why this is other goods than the near-identical card the check found. */
  distinct_reason?: string;
}

/**
 * Payload for updating an existing product.
 */
export interface UpdateProductPayload {
  name?: string;
  category_id?: string | null;
  price?: string | null;
  unit?: string;
  tax_rate?: string | null;
  scale_product_code?: string | null;
}

export interface CreateCategoryPayload {
  name: string;
}

export interface UpdateCategoryPayload {
  name: string;
}

// =============================================================================
// MUTATION FUNCTIONS
// =============================================================================

/**
 * Create a new product.
 */
export async function createProduct(payload: CreateProductPayload): Promise<ProductUpsertResponse> {
  return api.webAdmin.post<ProductUpsertResponse>('/products', payload);
}

export async function createCategory(payload: CreateCategoryPayload): Promise<CategoryListItem> {
  return api.webAdmin.post<CategoryListItem>('/products/categories', payload);
}

/**
 * Update an existing product.
 */
export async function updateProduct(
  id: string,
  payload: UpdateProductPayload,
): Promise<ProductUpsertResponse> {
  return api.webAdmin.patch<ProductUpsertResponse>(`/products/${id}`, payload);
}

export async function updateCategory(
  id: string,
  payload: UpdateCategoryPayload,
): Promise<CategoryListItem> {
  return api.webAdmin.patch<CategoryListItem>(`/products/categories/${id}`, payload);
}

export async function productUomLabelFix(
  productId: string,
  payload: { to_unit_code: string; reason: string },
): Promise<ProductUomLabelFixResponse> {
  return api.webAdmin.post<ProductUomLabelFixResponse>(
    `/products/${productId}/uom/label-fix`,
    payload,
  );
}

export type ProductUomPcsRounding = 'floor' | 'ceil' | 'nearest' | 'manual';

export async function productUomCutover(
  productId: string,
  payload: {
    to_unit_code: string;
    reason: string;
    new_balances_by_location: Record<string, string>;
    pcs_rounding?: ProductUomPcsRounding | null;
    cutover_at?: string | null;
  },
): Promise<ProductUomCutoverResponse> {
  return api.webAdmin.post<ProductUomCutoverResponse>(
    `/products/${productId}/uom/cutover`,
    payload,
  );
}

/**
 * Delete a product (soft delete).
 */
export async function deleteProduct(id: string): Promise<ProductDeleteResponse> {
  return api.webAdmin.delete<ProductDeleteResponse>(`/products/${id}`);
}

export async function createSubcategory(
  categoryId: string,
  payload: SubcategoryPayload & { name: string },
): Promise<SubcategoryListItem> {
  return api.webAdmin.post<SubcategoryListItem>(`/products/categories/${categoryId}/subcategories`, payload);
}

export async function updateSubcategory(id: string, payload: SubcategoryPayload): Promise<SubcategoryListItem> {
  return api.webAdmin.patch<SubcategoryListItem>(`/products/subcategories/${id}`, payload);
}

export async function deleteSubcategory(id: string): Promise<CategoryDeleteResponse> {
  return api.webAdmin.delete<CategoryDeleteResponse>(`/products/subcategories/${id}`);
}

/** Set or clear (null) the product's ERP subcategory; no GoPOS push. */
export async function setProductSubcategory(
  productId: string,
  subcategoryId: string | null,
): Promise<ProductSubcategoryResponse> {
  return api.webAdmin.put<ProductSubcategoryResponse>(`/products/${productId}/subcategory`, {
    subcategory_id: subcategoryId,
  });
}

export async function deleteCategory(id: string): Promise<CategoryDeleteResponse> {
  return api.webAdmin.delete<CategoryDeleteResponse>(`/products/categories/${id}`);
}

export async function deactivateCategory(
  id: string,
  reason: string,
): Promise<CategoryDeactivateResponse> {
  return api.webAdmin.post<CategoryDeactivateResponse>(`/products/categories/${id}/deactivate`, {
    cascade_products: true,
    reason,
  });
}

export async function activateProduct(id: string): Promise<ProductStatusResponse> {
  return api.webAdmin.post<ProductStatusResponse>(`/products/${id}/activate`);
}

export async function deactivateProduct(id: string): Promise<ProductStatusResponse> {
  return api.webAdmin.post<ProductStatusResponse>(`/products/${id}/deactivate`);
}

export async function addProductBarcode(
  id: string,
  payload: { barcode: string; type?: string | null },
): Promise<BarcodeCreateResponse> {
  return api.webAdmin.post<BarcodeCreateResponse>(`/products/${id}/barcodes`, payload);
}

export async function removeProductBarcode(
  id: string,
  barcodeId: string,
): Promise<BarcodeDeleteResponse> {
  return api.webAdmin.delete<BarcodeDeleteResponse>(`/products/${id}/barcodes/${barcodeId}`);
}

// =============================================================================
// AUDIT LOG
// =============================================================================

export interface ProductAuditLogItem {
  id: string;
  event_type: string;
  field_name: string | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  actor_user_id: string | null;
  source: string;
  created_at: string;
}

export interface ProductAuditLogResponse {
  request_id: string;
  items: ProductAuditLogItem[];
  total_count: number;
}

export async function fetchProductAuditLog(
  id: string,
  params: { limit?: number; offset?: number } = {},
): Promise<ProductAuditLogResponse> {
  return api.webAdmin.get<ProductAuditLogResponse>(`/products/${id}/audit-log`, {
    params: {
      limit: params.limit,
      offset: params.offset,
    },
  });
}

export function useProductAuditLog(
  id: string | null | undefined,
  params: { limit?: number; offset?: number } = {},
) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  return useQuery({
    queryKey: queryKeys.products.auditLog(id ?? '', limit, offset),
    queryFn: () => fetchProductAuditLog(id!, { limit, offset }),
    enabled: !!id,
  });
}

export async function fetchProductSyncStatus(id: string): Promise<ProductSyncStatusResponse> {
  return api.webAdmin.get<ProductSyncStatusResponse>(`/products/${id}/sync-status`);
}

export async function retryProductSync(id: string): Promise<ProductSyncStatusResponse> {
  return api.webAdmin.post<ProductSyncStatusResponse>(`/products/${id}/retry-sync`);
}

function invalidateProductCatalogQueries(queryClient: QueryClient, productId?: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.gopos.productsAll });
  if (productId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(productId) });
    // The name block shows the till name and its review state (MD-0120).
    queryClient.invalidateQueries({ queryKey: queryKeys.shelfLabels.product(productId) });
  }
}

export function invalidateProductCategoryQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: queryKeys.products.categories() });
  queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.gopos.categories() });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: (data) => {
      invalidateProductCatalogQueries(queryClient, data.id);
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCategory,
    onSuccess: () => {
      invalidateProductCategoryQueries(queryClient);
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateProductPayload }) =>
      updateProduct(id, payload),
    onSuccess: (_data, variables) => {
      invalidateProductCatalogQueries(queryClient, variables.id);
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCategoryPayload }) =>
      updateCategory(id, payload),
    onSuccess: () => {
      invalidateProductCategoryQueries(queryClient);
    },
  });
}

export function useProductUomLabelFix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: string;
      payload: { to_unit_code: string; reason: string };
    }) => productUomLabelFix(productId, payload),
    onSuccess: (_data, variables) => {
      invalidateProductCatalogQueries(queryClient, variables.productId);
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.balances.all });
    },
  });
}

export function useProductUomCutover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: string;
      payload: {
        to_unit_code: string;
        reason: string;
        new_balances_by_location: Record<string, string>;
        pcs_rounding?: ProductUomPcsRounding | null;
        cutover_at?: string | null;
      };
    }) => productUomCutover(productId, payload),
    onSuccess: (_data, variables) => {
      invalidateProductCatalogQueries(queryClient, variables.productId);
      queryClient.invalidateQueries({ queryKey: queryKeys.stock.balances.all });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      invalidateProductCatalogQueries(queryClient);
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      invalidateProductCategoryQueries(queryClient);
    },
  });
}

export function useCreateSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: string; payload: SubcategoryPayload & { name: string } }) =>
      createSubcategory(categoryId, payload),
    onSuccess: () => invalidateProductCategoryQueries(queryClient),
  });
}

export function useUpdateSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SubcategoryPayload }) => updateSubcategory(id, payload),
    onSuccess: () => invalidateProductCategoryQueries(queryClient),
  });
}

export function useDeleteSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSubcategory,
    onSuccess: () => invalidateProductCategoryQueries(queryClient),
  });
}

export function useSetProductSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, subcategoryId }: { productId: string; subcategoryId: string | null }) =>
      setProductSubcategory(productId, subcategoryId),
    onSuccess: (_data, variables) => {
      invalidateProductCatalogQueries(queryClient, variables.productId);
      queryClient.invalidateQueries({ queryKey: queryKeys.products.categories() });
    },
  });
}

export function useDeactivateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => deactivateCategory(id, reason),
    onSuccess: () => {
      invalidateProductCategoryQueries(queryClient);
    },
  });
}

export function useActivateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: activateProduct,
    onSuccess: (_data, productId) => {
      invalidateProductCatalogQueries(queryClient, productId);
    },
  });
}

export function useDeactivateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deactivateProduct,
    onSuccess: (_data, productId) => {
      invalidateProductCatalogQueries(queryClient, productId);
    },
  });
}

export function useAddProductBarcode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { barcode: string; type?: string | null };
    }) => addProductBarcode(id, payload),
    onSuccess: (_data, variables) => {
      invalidateProductCatalogQueries(queryClient, variables.id);
    },
  });
}

export function useRemoveProductBarcode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, barcodeId }: { id: string; barcodeId: string }) =>
      removeProductBarcode(id, barcodeId),
    onSuccess: (_data, variables) => {
      invalidateProductCatalogQueries(queryClient, variables.id);
    },
  });
}

export function useProductSyncStatus(
  id: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = options.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.products.syncStatus(id ?? ''),
    queryFn: () => fetchProductSyncStatus(id!),
    enabled: !!id && enabled,
  });
}

export function useRetryProductSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryProductSync,
    onSuccess: (_data, productId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.syncStatus(productId) });
      invalidateProductCatalogQueries(queryClient, productId);
    },
  });
}
